import type { ColumnsType } from 'antd/es/table';

import { CHART_METRIC_VIEW_TYPE } from '@/entities/chart/model/constants';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import type { ColumnBuilderParams } from '@/features/(charts)/chart-renderer/ui/Cross/types';
import TableCellRenderer from '@/features/(charts)/chart-renderer/ui/table-cell-element';

/**
 * Column 생성을 위한 베이스 클래스
 * 모든 ColumnBuilder는 이 클래스를 상속받아 구현
 */
export abstract class ColumnBuilder {
  protected params: ColumnBuilderParams;

  constructor(params: ColumnBuilderParams) {
    this.params = params;
  }

  /**
   * 컬럼을 생성하는 추상 메서드
   * 각 하위 클래스에서 구현해야 함
   */
  abstract buildColumns(): ColumnsType<any>;

  /**
   * 셀 렌더러를 생성하는 공통 메서드
   *
   * @param value - 셀 값 (null인 경우 '-'로 표시)
   * @param rowIndex - 행 인덱스
   * @param alias - 컬럼 alias
   * @param comparedTarget - 비교 대상 여부
   * @param totalCell - Total 셀 여부
   * @param seriesIndex - 시리즈 인덱스
   * @returns 렌더링된 셀 객체
   */
  protected createCellRenderer(
    value: number | null,
    rowIndex: number,
    alias: StringNumberValue,
    comparedTarget: boolean,
    totalCell?: boolean,
    seriesIndex?: number,
  ) {
    const { chart, configs, statistics, sortedCandidates, helpers } =
      this.params;
    const seriesOptions = chart.series[seriesIndex || 0]?.options;
    const primaryConfig = configs[seriesIndex ?? 0];

    const getBackgroundColor = () => {
      if (typeof value !== 'number') return 'transparent';
      if (totalCell) return 'transparent';
      if (!seriesOptions?.color_scale) return 'transparent';

      return chartUtils.getColorScaleColor(
        statistics,
        value,
        seriesOptions.color_scale,
        chart.series[seriesIndex || 0]?.options?.view_type ===
          CHART_METRIC_VIEW_TYPE.PERCENTAGE,
      );
    };

    const backgroundColor = getBackgroundColor();

    return {
      props: {
        style: {
          backgroundColor,
        },
      },
      children: (
        <TableCellRenderer
          value={value}
          isPercentage={primaryConfig?.isPercentage || false}
          onClick={() => {
            helpers.onCellClick(
              sortedCandidates[rowIndex] || null,
              alias,
              seriesOptions?.share_cell_calculation_segment || null,
              comparedTarget,
              seriesIndex || 0,
            );
          }}
          totalCell={totalCell}
        />
      ),
    };
  }
}
